import { ComponentProps } from 'lib/component-props';
import FundsFiltersSection from 'components/Pages/FundsCenter/AllFunds/FundsFilters/FundsFiltersSection';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import AccordionNew from 'components/Sections/Accordion/AccordionNew';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import { useI18n } from 'next-localization';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFundCentreData } from '@/store/fundCentre';
import { useLazyFetchAuthorisedFundsQuery } from '@/store/fundCentre/services/fundCentreAPI';
import { useLazyFetchAuthorisedFundLiteratureQuery } from '@/store/literatureHub/services/literatureHubAPI';
import styles from './AutomatedFundsLiteratureAccordion.module.scss';
import useHelpers from 'hooks/helpers';

const AutomatedFundsLiteratureAccordion = ({ fields }: ComponentProps): JSX.Element => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const { getErrorDictionary } = useHelpers();

  const [fundsData, setFundsData] = useState([]);
  const filters = fields?.value.filters.map((filter) => ({
    ...filter,
    displayName: t(filter.displayNameKey),
  }));
  const [getAuthorisedFundLiterature, { error }] = useLazyFetchAuthorisedFundLiteratureQuery();
  const [fetchAuthorisedFunds, { isFetching: isFundsLoading }] = useLazyFetchAuthorisedFundsQuery();

  const { searchQuery, selectedDate, selectedTab, paginationData } = useSelector(getFundCentreData);

  const {
    fundCentreConfiguration: {
      countryInvestors: { countryCode, investorType, literatureAuthorization, jurisdiction },
    },
    language,
    site: { name },
  } = sitecoreContext;

  const appliedFilters = useSelector(
    (state) => state.fundCentre.appliedFilters,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );

  const requestPayload = useMemo(() => {
    return {
      countryInvestors: {
        countryCode,
        investorType,
        jurisdiction,
        literatureAuthorization,
      },
      countryCode: countryCode,
      investorTypes: investorType,
      jurisdiction: jurisdiction,
      displayLanguage: language,
      language: language,
      site: name,
      literatureAuthorizations: [literatureAuthorization],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, searchQuery, paginationData, selectedTab, selectedDate]);

  const getDocuments = async (data, index: number) => {
    if (fundsData[index]?.documents) return;

    setFundsData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], loading: true };
      return updated;
    });

    try {
      const payload = {
        ...requestPayload,
        ...data,
      };

      const res = await getAuthorisedFundLiterature(payload);

      setFundsData((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          documents: res.data.content,
          loading: false,
          error: false,
        };
        return updated;
      });
    } catch (error: any) {
      console.error(error);
      setFundsData((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          error: {
            responseStatus: error?.response?.status,
            error: true,
          },
          loading: false,
        };
        return updated;
      });
    }
  };

  const getAuthorisedFunds = async () => {
    try {
      const payload = {
        language: requestPayload.displayLanguage,
        site: requestPayload.site,
        CountryInvestors: {
          countryCode: requestPayload.countryCode,
          investorType: requestPayload.investorTypes,
          jurisdiction: requestPayload.jurisdiction,
          literatureAuthorization: literatureAuthorization,
        },
        ...{ searchQuery: searchQuery },
        filters: appliedFilters,
      };
      const { data } = await fetchAuthorisedFunds(payload);
      setFundsData(data?.content?.fundLiterature);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAuthorisedFunds();
  }, [searchQuery, appliedFilters]);

  return (
    <section className="container mb-4 mb-lg-5">
      <FundsFiltersSection
        className="mb-4 mb-lg-5"
        filters={filters}
        showDatePicker={false}
        allowSearchByISIN={false}
        cookieName="automatedFundsLiterature"
        label="fundCentreLiteratureSearchHint"
        onFilterApply={getAuthorisedFunds}
      />

      {isExperienceEditor || isFundsLoading ? (
        <ul className={styles['documents']}>
          {[...Array(5)].map((item) => (
            <li key={item}>
              <div className="py-3">
                <BaseSkeleton className="d-block" width="120px" height="24px" />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        fundsData?.map((accordion, index) => (
          <AccordionNew
            key={accordion.id}
            title={{ value: accordion.name }}
            isFirst={index === 0}
            className="w-100"
            onClick={() => getDocuments(accordion, index)}
          >
            {isExperienceEditor || accordion.loading ? (
              <ul className={styles['documents']}>
                {[...Array(5)].map((item) => (
                  <li key={item}>
                    <div className="py-3">
                      <BaseSkeleton className="d-block" width="120px" height="24px" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : accordion.error ? (
              <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
            ) : (
              accordion.documents && (
                <ul className={styles['documents']}>
                  {accordion.documents.map((document, i) => (
                    <li key={i} className="py-3">
                      <a
                        href={document.documentUri}
                        target="_blank"
                        className="d-inline-flex align-items-center gap-1"
                      >
                        <span className="text-link">{document.title}</span>

                        <svg
                          className="link-icon"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.2417 5.35382L11.9124 6.68309H16.3655L9.4533 13.5288C9.18744 13.7947 9.18744 14.1935 9.4533 14.4593C9.71915 14.7252 10.1179 14.7252 10.3838 14.4593L17.296 7.68004V12.1331L18.6252 10.8038V5.35382H13.2417Z"
                            fill="currentColor"
                          />
                          <path
                            d="M17.9604 13.9939C17.5616 13.9939 17.2957 14.2598 17.2957 14.6585V17.3171H6.32927V6.68294H9.98476C10.3835 6.68294 10.6494 6.41708 10.6494 6.0183C10.6494 5.61952 10.3835 5.35367 9.98476 5.35367H5.66463C5.26585 5.35367 5 5.61952 5 6.0183V17.9817C5 18.3805 5.26585 18.6464 5.66463 18.6464H17.9604C18.3591 18.6464 18.625 18.3805 18.625 17.9817V14.6585C18.625 14.3262 18.2927 13.9939 17.9604 13.9939Z"
                            fill="currentColor"
                          />
                        </svg>

                        <span className="sr-only">{t('opensInNewWindow')}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )
            )}
          </AccordionNew>
        ))
      )}
    </section>
  );
};

export default AutomatedFundsLiteratureAccordion;
